import React, { useState } from 'react';
import { CheckCircle2, XCircle, MinusCircle, AlertCircle } from 'lucide-react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import CopyButton from './CopyButton';
import TestRunLogLink from './TestRunLogLink';
import './AddRouteModal.css';
import './McpTestModal.css';

// TESTING AN MCP SOURCE — the Test button on an mcp-proxy System box.
//
// THE SAME SHAPE AS THE OXI MODAL, because it answers the same question for a different protocol.
// There, you pick a real message, edit it and send it. Here you pick a TOOL, fill in its arguments and
// send that — and the two modes at the top are the peer of Push / Poll:
//
//   Call a tool     the day-to-day one: make a real call and read the answer
//   Run the checks  the sequence a client runs — discovery, challenge, handshake, catalogue —
//                   for when the question is "why won't Claude connect"
//
// THE FORM IS BUILT FROM THE TOOL'S OWN inputSchema. A hand-written form per tool would be twelve forms
// to keep in step with the catalogue, and the first one to drift would be the one nobody tested.

const icon = (step) => {
    if (step.skipped) return <MinusCircle size={15} className="mtm-skip" />;
    return step.ok
        ? <CheckCircle2 size={15} className="mtm-ok" />
        : <XCircle size={15} className="mtm-bad" />;
};

/** A schema property rendered as the control it deserves. Nothing clever — just not a raw JSON box. */
const Field = ({ name, schema, required, value, onChange }) => {
    const common = {
        id: `mcp-arg-${name}`,
        className: 'acm-field',
        value: value === undefined ? '' : value,
        onChange: (e) => onChange(name, e.target.value),
    };
    return (
        <label className="mtm-arg" htmlFor={`mcp-arg-${name}`}>
            <span className="acm-label">
                {name}
                {required && <span className="mtm-req" title="Required">*</span>}
                {schema.type && <span className="acm-hint"> {schema.type}</span>}
            </span>
            {schema.enum
                ? (
                    <select {...common}>
                        <option value="">—</option>
                        {schema.enum.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                )
                : <input {...common} placeholder={schema.pattern === '^\\d{4}-\\d{2}-\\d{2}$' ? 'YYYY-MM-DD' : ''} />}
            {schema.description && <span className="acm-hint mtm-arg-hint">{schema.description}</span>}
        </label>
    );
};

// TESTED AS ONE PROPERTY (V508). `implementation` is the row the button was pressed on: the server
// reads that implementation's credential for this source rather than whatever the template's node
// still carries, and defaults the hotel argument to its property. Absent — an integration with no
// implementations yet — the server falls back to the node, so an old design still tests.
const McpTestModal = ({ isOpen, onClose, integrationId, source, implementation }) => {
    const [mode, setMode] = useState('call');
    // THIS RUN'S OWN ID, minted here and sent with every action. The server tags each log entry with it,
    // so "open the logs for this run" is one exact filter rather than "this integration, recently".
    // Known before the answer comes back, so the link works even when the response is lost.
    const [transactionId] = useState(() => `HCONN-MCP-${Date.now()}`);
    const [token, setToken] = useState('');
    const [tools, setTools] = useState(null);
    const [tool, setTool] = useState('');
    const [args, setArgs] = useState({});
    const [busy, setBusy] = useState(false);
    const [minted, setMinted] = useState(null);   // { expiresIn, scopes, missingScope } — never the token
    const [checks, setChecks] = useState(null);
    const [called, setCalled] = useState(null);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const chosen = (tools || []).find(t => t.name === tool) || null;
    const props = (chosen && chosen.inputSchema && chosen.inputSchema.properties) || {};
    const required = (chosen && chosen.inputSchema && chosen.inputSchema.required) || [];

    const setArg = (name, value) => setArgs(a => ({ ...a, [name]: value }));

    // ARGUMENTS ARE SENT AS TYPED — but a number typed into a text box is a string, and a schema that
    // says integer means it. Converted here rather than by the server, which should receive what a real
    // client would send.
    const typedArgs = () => {
        const out = {};
        for (const [k, v] of Object.entries(args)) {
            if (v === '' || v === undefined) continue;
            const t = props[k] && props[k].type;
            if (t === 'integer' || t === 'number') { const n = Number(v); if (!Number.isNaN(n)) { out[k] = n; continue; } }
            if (t === 'array') { out[k] = String(v).split(',').map(x => x.trim()).filter(Boolean); continue; }
            out[k] = v;
        }
        return out;
    };

    // A TOKEN WITHOUT LEAVING THE SCREEN. This is a test system, and sending somebody off to a connector
    // to copy a bearer token before they can press Send is the kind of step that stops a thing being
    // tested at all. Minted from the credential this node already carries, by the same
    // client-credentials exchange a real client's backend would use.
    const mintToken = async () => {
        setBusy(true); setError(null); setMinted(null);
        try {
            const res = await integrationsService.testMcp(integrationId, {
                nodeId: source && source.id,
                implementationId: implementation && implementation.id,
                transactionId, action: 'token',
            });
            setToken(res.token || '');
            setMinted({ expiresIn: res.expiresIn, scopes: res.scopes || [], missingScope: res.missingScope });
            setTools(null);
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not mint a token'));
        } finally { setBusy(false); }
    };

    const loadTools = async () => {
        setBusy(true); setError(null);
        try {
            const res = await integrationsService.testMcp(integrationId, {
                nodeId: source && source.id,
                implementationId: implementation && implementation.id,
                transactionId, token: token.trim(), action: 'tools',
            });
            setTools(res.tools || []);
            if (res.tools && res.tools.length && !tool) setTool(res.tools[0].name);
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not read the tool catalogue'));
        } finally { setBusy(false); }
    };

    const callTool = async () => {
        setBusy(true); setError(null); setCalled(null);
        try {
            const res = await integrationsService.testMcp(integrationId, {
                nodeId: source && source.id,
                implementationId: implementation && implementation.id,
                transactionId, token: token.trim(), action: 'call',
                tool, arguments: typedArgs(),
            });
            setCalled(res);
        } catch (e) {
            setError(apiErrorMessage(e, 'The call could not be made'));
        } finally { setBusy(false); }
    };

    const runChecks = async () => {
        setBusy(true); setError(null); setChecks(null);
        try {
            const res = await integrationsService.testMcp(integrationId, {
                nodeId: source && source.id,
                implementationId: implementation && implementation.id,
                transactionId, token: token.trim() || undefined,
            });
            setChecks(res);
        } catch (e) {
            setError(apiErrorMessage(e, 'The checks could not be run'));
        } finally { setBusy(false); }
    };

    return (
        <div className="modal-overlay" onClick={() => !busy && onClose()}>
            <div className="modal-content modal-md acm-modal mtm" onClick={e => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-labelledby="mtm-title">
                <div className="modal-header">
                    <h2 id="mtm-title">
                        Test {source ? (source.adapter || source.system_name) : 'this MCP server'}
                        {implementation && <span className="acm-hint">
                            {' '}as {implementation.hotelname || implementation.name}
                        </span>}
                    </h2>
                    <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
                </div>

                <div className="modal-body mtm-body">
                    {/* The peer of the OXI modal's Push / Poll. */}
                    <div className="mtm-mode" role="radiogroup" aria-label="What to test">
                        <button type="button" role="radio" aria-checked={mode === 'call'}
                            className={mode === 'call' ? 'is-on' : ''} onClick={() => setMode('call')}>
                            Call a tool
                            <small>as a model would — a real request</small>
                        </button>
                        <button type="button" role="radio" aria-checked={mode === 'checks'}
                            className={mode === 'checks' ? 'is-on' : ''} onClick={() => setMode('checks')}>
                            Run the checks
                            <small>discovery, challenge, handshake, catalogue</small>
                        </button>
                    </div>

                    <label className="acm-label" htmlFor="mcp-token">
                        Access token {mode === 'checks' && <span className="acm-hint">optional</span>}
                    </label>
                    <textarea id="mcp-token" className="acm-field mtm-token" rows={2}
                        value={token} onChange={(e) => setToken(e.target.value)}
                        placeholder={mode === 'call'
                            ? 'A token is needed to call a tool. Take one from the connector after it signs in.'
                            : 'Without one, only discovery and the challenge are checked.'} />
                    <div className="mtm-token-actions">
                        <button type="button" className="btn btn-secondary" onClick={mintToken} disabled={busy}>
                            {busy ? 'Minting…' : 'Mint a test token'}
                        </button>
                        <span className="acm-hint">Never stored — used for this run and discarded.</span>
                    </div>

                    {/* WHAT A MINTED TOKEN CAN DO, SAID UP FRONT. It carries no user, so the server
                        refuses every write with it — that is the design, not a fault, and finding it out
                        from a refusal halfway through a booking test is the thing worth avoiding. */}
                    {minted && (
                        <div className="mtm-minted">
                            <strong>Token minted</strong>
                            {minted.expiresIn ? <span> · expires in {Math.round(minted.expiresIn / 60)} min</span> : null}
                            <div className="mtm-minted-note">
                                It authenticates as the integration, not as a person, so <strong>reads work and
                                writes are refused</strong>. To test a booking, paste a token from a connector
                                somebody has signed in to.
                            </div>
                            {minted.missingScope && (
                                <div className="mtm-minted-warn">
                                    This credential's token is missing the <code>{minted.missingScope}</code>{' '}
                                    scope, so the server will refuse it at the door. Add the scope to its app
                                    client.
                                </div>
                            )}
                        </div>
                    )}

                    {mode === 'call' ? (
                        <>
                            {!tools && (
                                <button type="button" className="btn btn-secondary mtm-load"
                                    onClick={loadTools} disabled={busy || !token.trim()}>
                                    {busy ? 'Reading…' : 'Read the tool catalogue'}
                                </button>
                            )}

                            {tools && tools.length === 0 && (
                                <p className="acm-hint">The server returned no tools.</p>
                            )}

                            {tools && tools.length > 0 && (
                                <>
                                    <label className="acm-label" htmlFor="mcp-tool" style={{ marginTop: '0.75rem' }}>Tool</label>
                                    <select id="mcp-tool" className="acm-field" value={tool}
                                        onChange={(e) => { setTool(e.target.value); setArgs({}); setCalled(null); }}>
                                        {tools.map(t => (
                                            <option key={t.name} value={t.name}>
                                                {t.name}{t.annotations && t.annotations.readOnlyHint === false ? '  — writes' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {chosen && <p className="acm-hint mtm-tool-desc">{chosen.description}</p>}

                                    {/* A WRITE SAYS SO BEFORE THE BUTTON IS REACHED FOR, not after. */}
                                    {chosen && chosen.annotations && chosen.annotations.readOnlyHint === false && (
                                        <p className="mtm-write-warn">
                                            <AlertCircle size={13} /> This tool changes the property&rsquo;s data.
                                        </p>
                                    )}

                                    {Object.keys(props).length > 0 && (
                                        <div className="mtm-args">
                                            {Object.entries(props).map(([name, schema]) => (
                                                <Field key={name} name={name} schema={schema}
                                                    required={required.includes(name)}
                                                    value={args[name]} onChange={setArg} />
                                            ))}
                                        </div>
                                    )}

                                    {/* WHAT WILL ACTUALLY BE SENT. The OXI modal shows the XML; this shows
                                        the JSON-RPC, for the same reason — a test you cannot read is a
                                        test you cannot trust. */}
                                    <label className="acm-label" style={{ marginTop: '0.75rem' }}>Request</label>
                                    <pre className="mtm-json">{JSON.stringify(
                                        { jsonrpc: '2.0', id: 1, method: 'tools/call', params: { name: tool, arguments: typedArgs() } },
                                        null, 2)}</pre>
                                </>
                            )}
                        </>
                    ) : (
                        <p className="acm-hint mtm-lede">
                            Runs the sequence a client runs and reports each step. The first failing row is
                            the answer.
                        </p>
                    )}

                    {error && <div className="os-msg os-msg-error mtm-error"><AlertCircle size={15} /> {error}</div>}

                    {/* ── the answer to a tool call ── */}
                    {mode === 'call' && called && (
                        <div className="mtm-result">
                            <div className="mtm-tally">
                                {called.error
                                    ? <span className="mtm-bad">could not reach the server</span>
                                    : <>
                                        <span className={called.isError ? 'mtm-bad' : 'mtm-ok'}>
                                            {called.isError ? 'the tool refused' : 'the tool answered'}
                                        </span>
                                        <span className="acm-hint">HTTP {called.status}</span>
                                    </>}
                                <span className="mtm-grow" />
                                <span className="acm-hint">{called.durationMs} ms</span>
                                <CopyButton value={called.text || called.raw || called.error} />
                            </div>
                            <pre className="mtm-json mtm-answer">{called.error || called.text || called.raw}</pre>
                            {/* WHAT THE PIPELINE THEN DID WITH IT — every step after the answer above,
                                on the Logs tab, filtered to this run. */}
                            <div className="mtm-loglink">
                                <TestRunLogLink integrationId={integrationId}
                                    transactionId={called.transactionId || transactionId} />
                            </div>
                        </div>
                    )}

                    {/* ── the answer to a conformance run ── */}
                    {mode === 'checks' && checks && (
                        <div className="mtm-result">
                            <div className="mtm-tally">
                                <strong>{checks.passed} passed</strong>
                                {checks.failed > 0 && <span className="mtm-bad">{checks.failed} failed</span>}
                                {checks.skipped > 0 && (
                                    <span className="mtm-skip">
                                        {checks.skipped} skipped{checks.tokenSupplied ? '' : ' — no token given'}
                                    </span>
                                )}
                                <span className="mtm-grow" />
                                <code className="mtm-endpoint">{checks.endpoint}</code>
                                <CopyButton value={checks.endpoint} />
                            </div>
                            <ol className="mtm-steps">
                                {(checks.steps || []).map(step => (
                                    <li key={step.name} className={step.ok === false ? 'mtm-step mtm-step-failed' : 'mtm-step'}>
                                        <div className="mtm-step-head">
                                            {icon(step)}
                                            <span className="mtm-step-title">{step.title}</span>
                                            {step.durationMs != null && <span className="mtm-step-ms">{step.durationMs} ms</span>}
                                        </div>
                                        {step.detail && <pre className="mtm-step-detail">{step.detail}</pre>}
                                    </li>
                                ))}
                            </ol>
                            <div className="mtm-loglink">
                                <TestRunLogLink integrationId={integrationId}
                                    transactionId={checks.transactionId || transactionId}
                                    label="Open the logs for this check run" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Close</button>
                    {mode === 'call' ? (
                        <button type="button" className="btn btn-primary" onClick={callTool}
                            disabled={busy || !tool || !token.trim()}>
                            {busy ? 'Sending…' : 'Send'}
                        </button>
                    ) : (
                        <button type="button" className="btn btn-primary" onClick={runChecks} disabled={busy}>
                            {busy ? 'Running…' : 'Run the checks'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default McpTestModal;
